import { useState, useEffect } from "react"
import { useSession } from "lib/auth/react"
import { MY_MOJO_API } from "../Config"
import { LogError } from "logging"

export type RiskIndividual = {
  age: number
  combinedRisk: number
  deathRisk: number
  incomeRisk: number
  name: string
  retirementAge: number
  illnessRisk: number
  sex: string
  smokerStatus: string
}

export type RiskJoint = {
  combinedRisk: number
  deathRisk: number
  incomeRisk: number
  illnessRisk: number
}

export type Advisor = {
  name: string
  role: string
  phone: string
  email: string
}

export type RiskReportData = {
  type: string
  createdAt: Date
  riskIndividual: Array<RiskIndividual>
  riskJoint: RiskJoint | null
  advisor: Advisor | null
}

export const useRiskReport = (friendlyId: string) => {
  const [riskReport, setRiskReport] = useState<RiskReportData>()
  const [isLoading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error>()

  // make sure the user session is available before calling an auth'd endpoint
  const { isLoading: sessionLoading } = useSession()

  useEffect(() => {
    if (sessionLoading) return

    fetch(`${MY_MOJO_API}/risk-report/${friendlyId}`, {
      credentials: "include",
    })
      .then((res) => {
        if (res.ok) return res.json()

        throw Error(`Failed to fetch risk report: ${res.status} ${res.statusText}`)
      })
      .then((json) => {
        setRiskReport(json)
        setLoading(false)
      })
      .catch((err) => {
        console.error(err)

        setError(err)
        setLoading(false)
      })
  }, [friendlyId, sessionLoading])

  return { riskReport, isLoading, error }
}

export const useRiskReportViewed = (friendlyId: string, isLoading: boolean, error?: Error) => {
  const [hasUpdated, setUpdated] = useState<boolean>(false)

  // make sure the user session is available before calling an auth'd endpoint
  const { isLoading: sessionLoading } = useSession()

  useEffect(() => {
    const isComingFromSf = document.referrer.includes(`salesforce.com`) // Advisors and Mojo'ers will come via Salesforce, so don't update then. Not perfect, but better than nothing
    const isReady = !sessionLoading && !isLoading && !error && !hasUpdated && !isComingFromSf
    if (!isReady) return

    fetch(`${MY_MOJO_API}/risk-report/${friendlyId}/viewed`, {
      credentials: "include",
      method: "PUT",
    })
      .then((res) => {
        if (!res.ok)
          throw Error(`Failed to mark risk report as viewed: ${res.status} ${res.statusText}`)

        setUpdated(true)
      })
      .catch((err) => {
        console.error(err)

        // Don't stop the user from viewing the report, just log a Sentry error
        LogError(err.message)
      })
  }, [sessionLoading, isLoading, error])
}
